import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import isEqual from 'lodash/fp/isEqual';

import { DEFAULT_PAGE_SIZE } from 'dashboard/lib/constants/pagination';
import { TableState } from 'dashboard/lib/interfaces/TableState';
import { TableViewsState } from 'dashboard/lib/interfaces/TableViewsState';
import { ApiLog, ApiLogAttributes } from 'dashboard/models/api-log';
import { Environment } from 'dashboard/models/environment';
import { ApiLogsTableView } from 'dashboard/models/table-view';
import { PaginationParams } from 'lib/interfaces/api';
import { Iso8601DateString } from 'lib/types';

type ApiLogs = Record<string, ApiLog[]>;

export interface ApiLogsFilterParams {
  createdAtStart?: Iso8601DateString;
  createdAtEnd?: Iso8601DateString;
  omni?: string;
  status?: ApiLogResponseStatus[];
  methods?: ApiLogAttributes['request']['method'][];
  apiKeyIds?: string[];
}

export enum ApiLogResponseStatus {
  Success = 'success',
  Error = 'error',
}

export interface ApiLogsState
  extends Omit<TableState<ApiLogsFilterParams>, 'sortParams'>,
    TableViewsState<ApiLogsTableView> {
  apiLogs: ApiLogs;
}

const initialState: ApiLogsState = {
  apiLogs: {},
  filters: {
    createdAtStart: undefined,
    createdAtEnd: undefined,
    omni: undefined,
  },
  pageSize: DEFAULT_PAGE_SIZE,
  currentPage: 1,
  tableViews: {
    views: [],
    currentTableView: '',
  },
};

const { reducer, actions } = createSlice({
  name: 'api-logs',
  initialState,
  reducers: {
    setApiLogs: (
      state,
      action: PayloadAction<{
        apiLogs: ApiLog[];
        environment: Environment;
        paginationParams?: PaginationParams;
      }>,
    ) => {
      state.apiLogs[action.payload.environment.id] = action.payload.apiLogs;
      state.paginationParams = action.payload.paginationParams;
    },
    setFilters: (state, action: PayloadAction<ApiLogsFilterParams>) => {
      const newFilters = {
        ...state.filters,
        ...action.payload,
      };

      if (!isEqual(newFilters, state.filters)) {
        state.filters = newFilters;
      }
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setTableViews: (state, action: PayloadAction<ApiLogsTableView[]>) => {
      state.tableViews.views = action.payload;
    },
    setCurrentTableView: (state, action: PayloadAction<string>) => {
      state.tableViews.currentTableView = action.payload;
    },
  },
});

export const {
  setApiLogs,
  setFilters,
  setPageSize,
  setCurrentPage,
  setTableViews,
  setCurrentTableView,
} = actions;
export const apiLogsReducer = reducer;
